import { Navigate } from "react-router-dom";
import AccessControl from "../pages/AccessControl";
import BackgroundChecks from "../pages/BackgroundChecks";
import ChatSpace from "../pages/ChatSpace";
import EmailMessages from "../pages/EmailMessages";
import Feedback from "../pages/Feedback";
import Help from "../pages/Help";
import Invitations from "../pages/Invitations";
import JobApplications from "../pages/JobApplications";
import JobPosts from "../pages/JobPosts";
import NewJobPost from "../pages/NewJobPost";
import OrganizationDetailsPage from "../pages/OrganizationDetailsPage";
import OrganizationsPage from "../pages/OrganizationsPage";
import PartnerCommissions from "../pages/PartnerCommissions";
import Partners from "../pages/Partners";
import ProfessionalProfiles from "../pages/ProfessionalProfiles";
import Profile from "../pages/Profile";
import Reports from "../pages/Reports";
import ScheduledInterviews from "../pages/ScheduledInterviews";
import Settings from "../pages/Settings";
import Support from "../pages/Support";
import TalentSearch from "../pages/TalentSearch";
import WebsiteBuild from "../pages/WebsiteBuild";
import WebsiteDetails from "../pages/WebsiteDetails";
import WebsiteDomain from "../pages/WebsiteDomain";
import WebsiteGenerating from "../pages/WebsiteGenerating";
import WebsiteSuccess from "../pages/WebsiteSuccess";
import WebsiteTemplates from "../pages/WebsiteTemplates";
import AssessmentProcessDetailPage from "../pages/care/AssessmentProcessDetailPage";
import AssessmentRecordDetailPage from "../pages/care/AssessmentRecordDetailPage";
import AssessmentRecordEditPage from "../pages/care/AssessmentRecordEditPage";
import AssessmentsPage from "../pages/care/AssessmentsPage";
import DashboardPage from "../pages/care/DashboardPage";
import HomePage from "../pages/care/HomePage";
import NegotiatedCarePlanPage from "../pages/care/NegotiatedCarePlanPage";
import NewAssessmentProcessPage from "../pages/care/NewAssessmentProcessPage";
import NewResidentPage from "../pages/care/NewResidentPage";
import ResidentDetailPage from "../pages/care/ResidentDetailPage";
import ResidentsListPage from "../pages/care/ResidentsListPage";
import WorkspaceChargesPage from "../workspaces/WorkspaceChargesPage";
import WorkspaceCreatePage from "../workspaces/WorkspaceCreatePage";
import WorkspaceDetailsPage from "../workspaces/WorkspaceDetailsPage";
import WorkspaceInvitationAcceptPage from "../workspaces/WorkspaceInvitationAcceptPage";
import WorkspaceInvitations from "../workspaces/WorkspaceInvitations";
import WorkspaceMemberDetailsPage from "../workspaces/WorkspaceMemberDetailsPage";
import WorkspaceOrganizationPage from "../workspaces/WorkspaceOrganizationPage";
import WorkspacePaymentMethodsPage from "../workspaces/WorkspacePaymentMethodsPage";
import WorkspaceSubscriptionsPage from "../workspaces/WorkspaceSubscriptionsPage";
import WorkspacesPage from "../workspaces/WorkspacesPage";
import { CareShell } from "../components/care/CareLayout";
import { useWorkspace } from "../contexts/WorkspaceContext";

function CurrentWorkspaceRedirect({ section }) {
  const { selectedWorkspaceId, loading, workspaceListReady } = useWorkspace();

  if (loading || !workspaceListReady) return null;
  if (!selectedWorkspaceId) return <Navigate to="/workspaces" replace />;
  return <Navigate to={`/workspaces/${selectedWorkspaceId}/${section}`} replace />;
}

function ShellRoute({ children }) {
  return <CareShell className="dashboard-content app-page-content">{children}</CareShell>;
}

const careRoutes = [
  { path: "/home", element: <HomePage /> },
  { path: "/dashboard", element: <DashboardPage /> },
  { path: "/assessments", element: <AssessmentsPage /> },
  { path: "/assessments/new", element: <NewAssessmentProcessPage /> },
  { path: "/assessments/:assessmentId/records/:recordId/edit", element: <AssessmentRecordEditPage /> },
  { path: "/assessments/:assessmentId/records/:recordId", element: <AssessmentRecordDetailPage /> },
  { path: "/assessments/:assessmentId", element: <AssessmentProcessDetailPage /> },
  { path: "/care-plans/negotiated", element: <NegotiatedCarePlanPage /> },
  { path: "/care-plans/negotiated/new-client/intake", element: <NegotiatedCarePlanPage screen="intake" /> },
  { path: "/care-plans/negotiated/new-client/meet-and-greet", element: <NegotiatedCarePlanPage screen="meet" /> },
  { path: "/care-plans/negotiated/new-client/preliminary-plan", element: <NegotiatedCarePlanPage screen="preliminary" /> },
  { path: "/care-plans/negotiated/new-client/probation", element: <NegotiatedCarePlanPage screen="probation" /> },
  { path: "/care-plans/negotiated/final-plan", element: <NegotiatedCarePlanPage screen="final" /> },
  { path: "/care-plans/negotiated/existing-client/annual-review", element: <NegotiatedCarePlanPage screen="annual" /> },
  { path: "/care-plans/negotiated/confirmed-files", element: <NegotiatedCarePlanPage screen="confirmed" /> },
  { path: "/residents", element: <ResidentsListPage /> },
  { path: "/residents/new", element: <NewResidentPage /> },
  { path: "/residents/:residentId/onboarding", element: <NewResidentPage /> },
  { path: "/residents/:residentId", element: <ResidentDetailPage /> },
];

const hireCareGiverRoutes = [
  { path: "/hire-care-giver", element: <Navigate to="/hire-care-giver/talent-search" replace /> },
  { path: "/hire-care-giver/talent-search", element: <ShellRoute><TalentSearch /></ShellRoute> },
  { path: "/hire-care-giver/job-posts", element: <ShellRoute><JobPosts /></ShellRoute> },
  { path: "/hire-care-giver/applications", element: <ShellRoute><JobApplications /></ShellRoute> },
  { path: "/hire-care-giver/interviews", element: <ShellRoute><ScheduledInterviews /></ShellRoute> },
  { path: "/hire-care-giver/job-posts/new", element: <ShellRoute><NewJobPost /></ShellRoute> },
  { path: "/hire-care-giver/professional-profiles", element: <ShellRoute><ProfessionalProfiles /></ShellRoute> },
  { path: "/hire-care-giver/background-checks", element: <ShellRoute><BackgroundChecks /></ShellRoute> },
  { path: "/hire-care-giver/reports", element: <ShellRoute><Reports /></ShellRoute> },
  { path: "/talent-search", element: <Navigate to="/hire-care-giver/talent-search" replace /> },
  { path: "/job-posts", element: <Navigate to="/hire-care-giver/job-posts" replace /> },
  { path: "/job-applications", element: <Navigate to="/hire-care-giver/applications" replace /> },
  { path: "/interviews", element: <Navigate to="/hire-care-giver/interviews" replace /> },
  { path: "/job-posts/new", element: <Navigate to="/hire-care-giver/job-posts/new" replace /> },
  { path: "/professional-profiles", element: <Navigate to="/hire-care-giver/professional-profiles" replace /> },
  { path: "/background-checks", element: <Navigate to="/hire-care-giver/background-checks" replace /> },
  { path: "/reports", element: <Navigate to="/hire-care-giver/reports" replace /> },
];

const workspaceRoutes = [
  { path: "/workspaces", element: <ShellRoute><WorkspacesPage /></ShellRoute> },
  { path: "/workspaces/new", element: <ShellRoute><WorkspaceCreatePage /></ShellRoute> },
  { path: "/workspaces/invitations/accept", element: <ShellRoute><WorkspaceInvitationAcceptPage /></ShellRoute> },
  { path: "/workspaces/:workspaceId", element: <ShellRoute><WorkspaceDetailsPage /></ShellRoute> },
  { path: "/workspaces/:workspaceId/invitations/:invitationId", element: <ShellRoute><WorkspaceInvitations /></ShellRoute> },
  { path: "/workspaces/:workspaceId/members/:userId", element: <ShellRoute><WorkspaceMemberDetailsPage /></ShellRoute> },
  { path: "/workspaces/:workspaceId/organization", element: <ShellRoute><WorkspaceOrganizationPage /></ShellRoute> },
  { path: "/workspaces/current/payment-methods", element: <CurrentWorkspaceRedirect section="payment-methods" /> },
  { path: "/workspaces/current/charges", element: <CurrentWorkspaceRedirect section="charges" /> },
  { path: "/workspaces/current/subscriptions", element: <CurrentWorkspaceRedirect section="subscriptions" /> },
  { path: "/workspaces/:workspaceId/payment-methods", element: <ShellRoute><WorkspacePaymentMethodsPage /></ShellRoute> },
  { path: "/workspaces/:workspaceId/charges", element: <ShellRoute><WorkspaceChargesPage /></ShellRoute> },
  { path: "/workspaces/:workspaceId/subscriptions", element: <ShellRoute><WorkspaceSubscriptionsPage /></ShellRoute> },
];

const appUtilityRoutes = [
  { path: "/profile", element: <ShellRoute><Profile /></ShellRoute> },
  { path: "/invitations", element: <ShellRoute><Invitations /></ShellRoute> },
  { path: "/website", element: <ShellRoute><WebsiteDetails /></ShellRoute> },
  { path: "/website/templates", element: <ShellRoute><WebsiteTemplates /></ShellRoute> },
  { path: "/website/build/:templateId", element: <ShellRoute><WebsiteBuild /></ShellRoute> },
  { path: "/website/regenerate", element: <ShellRoute><WebsiteTemplates regenerate /></ShellRoute> },
  { path: "/website/generating/:websiteId", element: <ShellRoute><WebsiteGenerating /></ShellRoute> },
  { path: "/website/success/:websiteId", element: <ShellRoute><WebsiteSuccess /></ShellRoute> },
  { path: "/website/domain", element: <ShellRoute><WebsiteDomain /></ShellRoute> },
  { path: "/organizations", element: <ShellRoute><OrganizationsPage /></ShellRoute> },
  { path: "/organizations/:organizationId", element: <ShellRoute><OrganizationDetailsPage /></ShellRoute> },
  { path: "/access-control", element: <ShellRoute><AccessControl /></ShellRoute> },
  { path: "/settings", element: <ShellRoute><Settings /></ShellRoute> },
  { path: "/chat-space", element: <ShellRoute><ChatSpace /></ShellRoute> },
  { path: "/email-messages", element: <ShellRoute><EmailMessages /></ShellRoute> },
  { path: "/app/help", element: <ShellRoute><Help /></ShellRoute> },
  { path: "/support", element: <ShellRoute><Support /></ShellRoute> },
  { path: "/feedback", element: <ShellRoute><Feedback /></ShellRoute> },
  { path: "/partners", element: <ShellRoute><Partners /></ShellRoute> },
  { path: "/partners/commissions", element: <ShellRoute><PartnerCommissions /></ShellRoute> },
];

export const protectedRoutes = [
  ...careRoutes,
  ...hireCareGiverRoutes,
  ...workspaceRoutes,
  ...appUtilityRoutes,
];
